import { useEffect } from "react";

/**
 * Stop unsaved work disappearing.
 *
 * Two ways to lose a half-filled form, and they need different handling.
 * Closing the tab or hitting the browser's back button is the browser's
 * business, so that goes through beforeunload. Clicking "Vehicles" in the
 * sidebar is ours, and the browser knows nothing about it — so the dirty
 * state is registered here and App asks before it switches screens.
 *
 * Each guard registers itself under its own key rather than bumping a shared
 * counter. The vehicle page holds several forms at once — the listing, an
 * expense, the edit form — and a counter cannot tell "the expense just saved"
 * from "everything on this page is safe", so saving one used to stand down the
 * warning for all of them.
 */

const active = new Map<symbol, string>();

function warn(e: BeforeUnloadEvent) {
  if (active.size === 0) return;
  // Browsers ignore custom text now and show their own wording. Calling
  // preventDefault is what actually triggers the prompt.
  e.preventDefault();
  e.returnValue = "";
}
if (typeof window !== "undefined") window.addEventListener("beforeunload", warn);

export function hasUnsavedWork() {
  return active.size > 0;
}

export function unsavedMessage() {
  const whats = Array.from(new Set(active.values()));
  if (whats.length === 0) return "You have unsaved changes.";
  return `You have not saved ${whats.join(", ")}.`;
}

/**
 * Ask before throwing away unsaved work. True when there is nothing to lose
 * or the person said to leave anyway.
 */
export function confirmLeave(): boolean {
  if (!hasUnsavedWork()) return true;
  const ok = window.confirm(`${unsavedMessage()} Leave this page anyway?`);
  if (ok) clearUnsavedWork();
  return ok;
}

/**
 * Stand every guard down now, synchronously.
 *
 * For the navigation that is supposed to happen: a form that has just saved
 * is still full of text, so leaving it looks identical to abandoning it.
 * Setting a piece of React state cannot do this job — the state update is
 * queued, the effect cleanup runs after the next render, and the navigation
 * happens in between. So the registry is cleared directly.
 */
export function clearUnsavedWork() {
  active.clear();
}

/**
 * Call with true while a form holds work that is not yet saved.
 *
 * @param dirty   whether there is anything to lose
 * @param what    what would be lost, for the message — "this customer"
 */
export function useUnsavedGuard(dirty: boolean, what = "these changes") {
  useEffect(() => {
    if (!dirty) return;
    const key = Symbol(what);
    active.set(key, what);
    return () => { active.delete(key); };
  }, [dirty, what]);
}
